import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Router } from '@angular/router';
import { TokenStorageService } from './core/service/token-storage.service';
import { IdleWarningDialogComponent } from './idle-warning-dialog/idle-warning-dialog.component';
import { environment } from '../environments/environment';

type IdleDialogResult = 'continue' | 'logout' | 'dismiss';

@Injectable({
  providedIn: 'root'
})
export class IdleService {
  private dialogRef: MatDialogRef<IdleWarningDialogComponent> | null = null;

  constructor(
    private idle: Idle,
    private dialog: MatDialog,
    private tokenStorage: TokenStorageService,
    private router: Router
  ) {
    this.idle.setIdle(environment.sessionIdleSeconds);
    this.idle.setTimeout(environment.sessionWarningSeconds);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.showWarning(countdown);
    });

    this.idle.onIdleEnd.subscribe(() => this.continueSession());
    this.idle.onTimeout.subscribe(() => this.logout());
  }

  private showWarning(countdown: number): void {
    if (this.dialogRef) {
      this.dialogRef.componentInstance.countdown = countdown;
      return;
    }

    const openedDialog = this.dialog.open(IdleWarningDialogComponent, {
      disableClose: true,
      width: '460px',
      panelClass: 'idle-dialog-panel',
      data: { countdown }
    });
    this.dialogRef = openedDialog;

    openedDialog.afterClosed().subscribe((result: IdleDialogResult | undefined) => {
      if (this.dialogRef === openedDialog) {
        this.dialogRef = null;
      }

      if (result === 'continue') {
        this.resetTimer();
      } else if (result === 'logout') {
        this.logout();
      }
    });
  }

  private closeDialog(result: IdleDialogResult = 'dismiss'): void {
    const dialogRef = this.dialogRef;
    this.dialogRef = null;
    if (dialogRef) {
      dialogRef.close(result);
    }
  }

  resetTimer(): void {
    if (!this.tokenStorage.getToken()) {
      this.stopTimer();
      return;
    }

    this.idle.watch();
  }

  continueSession(): void {
    if (!this.tokenStorage.getToken()) {
      this.stopTimer();
      return;
    }

    this.closeDialog('dismiss');
    this.idle.watch();
  }

  stopTimer(): void {
    this.idle.stop();
    this.closeDialog('dismiss');
  }

  logout(): void {
    this.idle.stop();
    this.closeDialog('dismiss');
    this.tokenStorage.signOut();
    this.router.navigate(['/authentication/signin'], {
      queryParams: { reason: 'session-timeout' }
    });
  }
}
